/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useNavigate, Link } from 'react-router-dom';
import Navbar from '../Navbar';
import Header from '../Header';
import Loader from '../Loader';
import baseUrl from '../BaseUrl';
import NotificationForm from './NotificationForm';
import SendCsvNotificationForm from './SendCsvNotificationForm';

const TYPE_FILTER_TABS = [
  { value: '', label: 'All' },
  { value: 'post_signup', label: 'Post-Signup' },
  { value: 'paid_user', label: 'Paid User' },
  { value: 'date_specific', label: 'Date-Specific' },
];

const TYPE_LABEL = {
  post_signup: 'Post-Signup',
  paid_user: 'Paid User',
  date_specific: 'Date-Specific',
};

function truncate(str, max) {
  if (!str) return '';
  return str.length > max ? str.slice(0, max) + '…' : str;
}

const AUDIENCE_LABEL = { all: 'All Users', paid: 'Paid Users', free: 'Free Users' };

function formatDayDate(config) {
  if (config.type === 'date_specific') {
    if (!config.scheduled_date) return '—';
    const dateStr = String(config.scheduled_date).slice(0, 10);
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      return `${Number(parts[2])}/${Number(parts[1])}/${parts[0]}`;
    }
    return dateStr;
  }
  if (config.day_offset != null) {
    return config.type === 'post_signup'
      ? `Day ${config.day_offset}`
      : `${config.day_offset} day${config.day_offset === 1 ? '' : 's'} after purchase`;
  }
  return '—';
}

function StatusBadge({ status }) {
  if (status === 'Active') {
    return (
      <span
        style={{
          background: '#d1fae5',
          color: '#065f46',
          border: '1px solid #6ee7b7',
          borderRadius: '12px',
          padding: '2px 10px',
          fontSize: '12px',
          fontWeight: 600,
          whiteSpace: 'nowrap',
        }}
      >
        Active
      </span>
    );
  }
  if (status === 'Inactive') {
    return (
      <span
        style={{
          background: '#fef9c3',
          color: '#854d0e',
          border: '1px solid #fde68a',
          borderRadius: '12px',
          padding: '2px 10px',
          fontSize: '12px',
          fontWeight: 600,
          whiteSpace: 'nowrap',
        }}
      >
        Inactive
      </span>
    );
  }
  // Expired
  return (
    <span
      style={{
        background: '#f3f4f6',
        color: '#6b7280',
        border: '1px solid #d1d5db',
        borderRadius: '12px',
        padding: '2px 10px',
        fontSize: '12px',
        fontWeight: 600,
        whiteSpace: 'nowrap',
      }}
    >
      Expired
    </span>
  );
}

const NotificationList = () => {
  const navigate = useNavigate();
  const formRef = useRef(null);

  const [name, setName] = useState('');
  const [token, setToken] = useState('');
  const [expire, setExpire] = useState('');
  const [loading, setLoading] = useState(false);
  const [configs, setConfigs] = useState([]);
  const [editConfig, setEditConfig] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showCsvForm, setShowCsvForm] = useState(false);
  const [filterType, setFilterType] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    refreshToken();
  }, []);

  useEffect(() => {
    if (successMsg) {
      const t = setTimeout(() => setSuccessMsg(''), 4000);
      return () => clearTimeout(t);
    }
  }, [successMsg]);

  useEffect(() => {
    if (errorMsg) {
      const t = setTimeout(() => setErrorMsg(''), 4000);
      return () => clearTimeout(t);
    }
  }, [errorMsg]);

  const refreshToken = async () => {
    try {
      const response = await axios.get(`${baseUrl}/admin/api/token`);
      setToken(response.data.accessToken);
      const decoded = jwtDecode(response.data.accessToken);
      setName(decoded.name);
      setExpire(decoded.exp);
    } catch (error) {
      navigate('/');
    }
  };

  const axiosJWT = axios.create();

  axiosJWT.interceptors.request.use(
    async (config) => {
      const currentDate = new Date();
      if (expire * 1000 < currentDate.getTime()) {
        try {
          const response = await axios.get(`${baseUrl}/admin/api/token`);
          config.headers.Authorization = `Bearer ${response.data.accessToken}`;
          setToken(response.data.accessToken);
          const decoded = jwtDecode(response.data.accessToken);
          setName(decoded.name);
          setExpire(decoded.exp);
        } catch (err) {
          navigate('/');
        }
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  const fetchConfigs = async (type) => {
    setLoading(true);
    try {
      const params = type ? `?type=${type}` : '';
      const response = await axiosJWT.get(`${baseUrl}/admin/api/notification-configs${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        setConfigs(response.data.data || []);
      }
    } catch (err) {
      setErrorMsg(err?.response?.data?.message || 'Failed to fetch notification configs.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchConfigs(filterType);
    }
  }, [token, filterType]);

  const handleTabClick = (value) => {
    setFilterType(value);
  };

  const handleAddClick = () => {
    setEditConfig(null);
    setShowCsvForm(false);
    setShowForm(true);
    setTimeout(() => {
      if (formRef.current) {
        formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleCsvClick = () => {
    setShowForm(false);
    setEditConfig(null);
    setShowCsvForm(true);
    setTimeout(() => {
      if (formRef.current) {
        formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleEditClick = (config) => {
    setEditConfig(config);
    setShowForm(true);
    setTimeout(() => {
      if (formRef.current) {
        formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleFormSave = () => {
    setSuccessMsg(editConfig ? 'Notification config updated.' : 'Notification config created.');
    setShowForm(false);
    setEditConfig(null);
    fetchConfigs(filterType);
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditConfig(null);
  };

  const handleToggle = async (id) => {
    try {
      const response = await axiosJWT.patch(
        `${baseUrl}/admin/api/notification-config/toggle`,
        { id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        const newActive = response.data.data?.is_active;
        setSuccessMsg(newActive === 1 ? 'Notification activated.' : 'Notification deactivated.');
        fetchConfigs(filterType);
      } else {
        setErrorMsg(response.data.message || 'Toggle failed.');
      }
    } catch (err) {
      setErrorMsg(err?.response?.data?.message || 'Failed to toggle notification.');
    }
  };

  const handleDelete = async (id) => {
    const confirmed = window.confirm('Are you sure you want to delete this notification config?');
    if (!confirmed) return;
    try {
      const response = await axiosJWT.delete(`${baseUrl}/admin/api/notification-config?id=${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        setSuccessMsg('Notification config deleted.');
        fetchConfigs(filterType);
      } else {
        setErrorMsg(response.data.message || 'Delete failed.');
      }
    } catch (err) {
      setErrorMsg(err?.response?.data?.message || 'Failed to delete notification config.');
    }
  };

  return (
    <div className="panelbox">
      <Navbar />
      <div className="right-panel">
        <Header name={name} />
        {loading && <Loader />}
        <div className="content-page">
          {/* Page header row */}
          <div className="row" style={{ alignItems: 'center' }}>
            <div className="col-md-6 col-12">
              <div className="left-title">
                <h1>Notifications</h1>
              </div>
            </div>
            <div className="col-md-6 col-12" style={{ textAlign: 'right' }}>
              {!showForm && !showCsvForm && (
                <>
                  <button
                    type="button"
                    onClick={handleAddClick}
                    style={{
                      background: '#FE825B',
                      color: '#fff',
                      border: '1px solid #FE825B',
                      borderRadius: '5px',
                      padding: '9px 14px',
                      fontSize: '16px',
                      cursor: 'pointer',
                      marginRight: '10px',
                    }}
                  >
                    + Add Notification
                  </button>
                  <button
                    type="button"
                    onClick={handleCsvClick}
                    style={{
                      background: '#fff',
                      color: '#FE825B',
                      border: '1px solid #FE825B',
                      borderRadius: '5px',
                      padding: '9px 14px',
                      fontSize: '16px',
                      cursor: 'pointer',
                      marginRight: '10px',
                    }}
                  >
                    Send via CSV
                  </button>
                </>
              )}
              <div className="addnew-button" style={{ display: 'inline-block' }}>
                <Link to="/dashboard">
                  <span>&larr;</span> Back
                </Link>
              </div>
            </div>
          </div>

          {/* Global banners */}
          {successMsg && (
            <div
              role="status"
              style={{
                background: '#d1fae5',
                border: '1px solid #6ee7b7',
                color: '#065f46',
                borderRadius: '6px',
                padding: '10px 14px',
                marginBottom: '14px',
              }}
            >
              {successMsg}
            </div>
          )}
          {errorMsg && (
            <div
              role="alert"
              style={{
                background: '#fee2e2',
                border: '1px solid #fca5a5',
                color: '#991b1b',
                borderRadius: '6px',
                padding: '10px 14px',
                marginBottom: '14px',
              }}
            >
              {errorMsg}
            </div>
          )}

          {/* Form (shown above table when active) */}
          {showForm && (
            <div ref={formRef}>
              <NotificationForm
                token={token}
                editConfig={editConfig}
                onSave={handleFormSave}
                onCancel={handleFormCancel}
              />
            </div>
          )}

          {/* CSV-upload send panel */}
          {showCsvForm && (
            <div ref={formRef}>
              <SendCsvNotificationForm
                token={token}
                onCancel={() => setShowCsvForm(false)}
              />
            </div>
          )}

          {/* Type filter tabs */}
          <div
            className="row"
            style={{ marginBottom: '16px' }}
          >
            <div className="col-md-12">
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {TYPE_FILTER_TABS.map((tab) => (
                  <button
                    key={tab.value}
                    type="button"
                    onClick={() => handleTabClick(tab.value)}
                    style={{
                      padding: '6px 16px',
                      borderRadius: '6px',
                      border: '1px solid',
                      cursor: 'pointer',
                      fontWeight: filterType === tab.value ? 700 : 400,
                      background: filterType === tab.value ? '#FE825B' : '#fff',
                      color: filterType === tab.value ? '#fff' : '#FE825B',
                      borderColor: '#FE825B',
                      transition: 'background 0.15s, color 0.15s',
                    }}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Configs table */}
          <div className="row">
            <div className="col-md-12">
              <div className="dashboard-box" style={{ padding: '0', overflowX: 'auto' }}>
                {configs.length === 0 && !loading ? (
                  <div
                    style={{
                      padding: '40px',
                      textAlign: 'center',
                      color: '#6b7280',
                      fontSize: '15px',
                    }}
                  >
                    No notifications configured
                    {filterType ? ` for type "${TYPE_LABEL[filterType] || filterType}"` : ''}.
                  </div>
                ) : (
                  <table
                    className="table"
                    style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 0 }}
                  >
                    <thead>
                      <tr
                        style={{
                          background: '#FE825B',
                          color: '#fff',
                          fontSize: '13px',
                          fontWeight: 600,
                        }}
                      >
                        <th style={{ padding: '12px 16px', textAlign: 'left', whiteSpace: 'nowrap' }}>Type</th>
                        <th style={{ padding: '12px 16px', textAlign: 'left' }}>Title</th>
                        <th style={{ padding: '12px 16px', textAlign: 'left' }}>Body</th>
                        <th style={{ padding: '12px 16px', textAlign: 'left', whiteSpace: 'nowrap' }}>Screen</th>
                        <th style={{ padding: '12px 16px', textAlign: 'left', whiteSpace: 'nowrap' }}>Day / Date</th>
                        <th style={{ padding: '12px 16px', textAlign: 'left', whiteSpace: 'nowrap' }}>Target</th>
                        <th style={{ padding: '12px 16px', textAlign: 'left', whiteSpace: 'nowrap' }}>Status</th>
                        <th style={{ padding: '12px 16px', textAlign: 'left', whiteSpace: 'nowrap' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {configs.map((config, idx) => {
                        const isExpired = config.status_display === 'Expired';
                        const rowStyle = isExpired
                          ? { background: '#f9fafb', color: '#9ca3af' }
                          : idx % 2 === 0
                          ? { background: '#fff' }
                          : { background: '#f8fafc' };

                        return (
                          <tr key={config.id} style={{ ...rowStyle, borderBottom: '1px solid #e5e7eb' }}>
                            <td style={{ padding: '12px 16px', whiteSpace: 'nowrap', fontSize: '13px' }}>
                              <span
                                style={{
                                  background: '#e0e7ef',
                                  color: '#1e3a5f',
                                  borderRadius: '10px',
                                  padding: '2px 9px',
                                  fontSize: '12px',
                                  fontWeight: 600,
                                }}
                              >
                                {TYPE_LABEL[config.type] || config.type}
                              </span>
                            </td>
                            <td style={{ padding: '12px 16px', maxWidth: '180px' }}>
                              {config.title || '—'}
                            </td>
                            <td style={{ padding: '12px 16px', maxWidth: '220px', color: isExpired ? '#9ca3af' : '#374151' }}>
                              {truncate(config.body, 50)}
                            </td>
                            <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                              {config.screen_name || '—'}
                            </td>
                            <td style={{ padding: '12px 16px', whiteSpace: 'nowrap', fontSize: '13px' }}>
                              {formatDayDate(config)}
                            </td>
                            <td style={{ padding: '12px 16px', whiteSpace: 'nowrap', fontSize: '13px' }}>
                              {config.type === 'date_specific'
                                ? (AUDIENCE_LABEL[config.target_audience] || 'All Users')
                                : '—'}
                            </td>
                            <td style={{ padding: '12px 16px' }}>
                              <StatusBadge status={config.status_display} />
                            </td>
                            <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                              <button
                                type="button"
                                title="Edit"
                                onClick={() => handleEditClick(config)}
                                style={{
                                  marginRight: '8px',
                                  background: 'none',
                                  border: '1px solid #3b82f6',
                                  color: '#3b82f6',
                                  borderRadius: '5px',
                                  padding: '4px 10px',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  fontWeight: 600,
                                }}
                              >
                                Edit
                              </button>
                              {!isExpired && (
                                <button
                                  type="button"
                                  title={config.is_active === 1 ? 'Deactivate' : 'Activate'}
                                  onClick={() => handleToggle(config.id)}
                                  style={{
                                    marginRight: '8px',
                                    background: 'none',
                                    border: `1px solid ${config.is_active === 1 ? '#f59e0b' : '#10b981'}`,
                                    color: config.is_active === 1 ? '#f59e0b' : '#10b981',
                                    borderRadius: '5px',
                                    padding: '4px 10px',
                                    cursor: 'pointer',
                                    fontSize: '12px',
                                    fontWeight: 600,
                                  }}
                                >
                                  {config.is_active === 1 ? 'Deactivate' : 'Activate'}
                                </button>
                              )}
                              <button
                                type="button"
                                title="Delete"
                                onClick={() => handleDelete(config.id)}
                                style={{
                                  background: 'none',
                                  border: '1px solid #ef4444',
                                  color: '#ef4444',
                                  borderRadius: '5px',
                                  padding: '4px 10px',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  fontWeight: 600,
                                }}
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationList;
